import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Card, PageHeader, Btn, Stat, Pill } from "../../components/UI";
import { HandCoins, Users, HeartHandshake, ArrowRight, LayoutTemplate, Sparkles, Megaphone } from "lucide-react";

const usd = (n) => "$" + new Intl.NumberFormat().format(Math.round(n || 0));

export default function FundraisingDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => { (async () => {
    const [o, c] = await Promise.all([api.get("/fundraising/overview"), api.get("/fundraising/campaigns")]);
    setOverview(o.data); setCampaigns(c.data);
  })(); }, []);
  if (!overview) return <div className="p-8 text-slate-400">Loading…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Fundraising OS"
        title="Build. Raise. Promote. Measure."
        subtitle="A separate but tightly-integrated product sharing your political entity, branding, and compliance."
        action={
          <div className="flex gap-2">
            <Btn variant="secondary" onClick={() => navigate("/ai-landing-page")}><Sparkles className="w-4 h-4" /> AI Landing Page</Btn>
            <Btn onClick={() => navigate("/fundraising/campaigns")}><HandCoins className="w-4 h-4" /> New fundraiser</Btn>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="fr-kpi-raised" label="Raised" value={usd(overview.raised_amount)} icon={HandCoins} />
        <Stat testid="fr-kpi-goal" label="Goal" value={usd(overview.goal_amount)} icon={LayoutTemplate} />
        <Stat testid="fr-kpi-donors" label="Donors" value={overview.total_donors.toLocaleString()} icon={Users} />
        <Stat testid="fr-kpi-avg" label="Avg contribution" value={usd(overview.avg_contribution)} icon={HeartHandshake} />
      </div>

      <Card padded={false}>
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">Active fundraisers</h3>
          <Btn variant="ghost" onClick={() => navigate("/fundraising/campaigns")}>View all →</Btn>
        </div>
        {campaigns.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">No fundraising campaigns yet.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {campaigns.map(c => {
              const pct = c.goal_amount ? Math.min(100, (c.raised_amount / c.goal_amount) * 100) : 0;
              return (
                <div key={c.id} className="p-5 hover:bg-white/[0.02] cursor-pointer" onClick={() => navigate(`/fundraising/campaigns/${c.id}`)}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-display text-lg font-bold text-white truncate">{c.name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{c.total_donors} donors · {usd(c.raised_amount)} raised of {usd(c.goal_amount)}</div>
                    </div>
                    <Pill status={c.status}>{c.status}</Pill>
                  </div>
                  <div className="mt-3 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card className="relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-cyan-400/10 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-cyan-300 font-medium">Cross-product</div>
            <h3 className="font-display text-2xl font-bold mt-1">Promote this fundraiser with CTV.</h3>
            <p className="text-sm text-slate-400 mt-1">Turn any fundraiser into a political advertising campaign. Same entity, same branding, unified attribution.</p>
          </div>
          <Btn onClick={() => navigate("/campaigns/ctv/new")} data-testid="fr-promote-cta">
            <Megaphone className="w-4 h-4" /> Promote via CTV <ArrowRight className="w-4 h-4" />
          </Btn>
        </div>
      </Card>
    </div>
  );
}
